'use client';

import type { MouseEvent as ReactMouseEvent } from 'react';
import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { Textarea } from '@savvy-kit/components/textarea';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { StickyNote } from 'lucide-react';
import { graphql } from 'src/gql';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

const UpdateAccountCustomNoteMutation = graphql(/* GraphQL */ `
  mutation UpdateAccountCustomNoteFromTable($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
        customNote
      }
    }
  }
`);

type Props = {
  readonly accountId: string;
  readonly customNote: string | null | undefined;
};

export function AccountCustomNotePopover({ accountId, customNote }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(customNote ?? '');
  const [updateAccount, { loading }] = useMutationWithErrorLogging(UpdateAccountCustomNoteMutation);

  const hasNote = !isNil(customNote) && customNote.length > 0;

  const handleSave = async () => {
    const trimmed = draft.trim();
    await updateAccount({
      variables: {
        input:
          trimmed.length === 0 ? { id: accountId, deleteCustomNote: true } : { id: accountId, customNote: trimmed },
      },
    });
    setOpen(false);
  };

  const stopRowClick = (e: ReactMouseEvent) => e.stopPropagation();

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setDraft(customNote ?? '');
        setOpen(next);
      }}
    >
      <PopoverTrigger
        render={
          <button
            type="button"
            onClick={stopRowClick}
            aria-label={hasNote ? 'View or edit account note' : 'Add an account note'}
            className={cn(
              'inline-flex items-center justify-center rounded transition-opacity p-0.5',
              'text-secondary hover:text-dark focus-visible:outline-2 focus-visible:outline-primary',
              // Always visible when there's a note; hover-only when empty.
              hasNote ? 'opacity-100' : 'opacity-0 group-hover/account-row:opacity-100 focus:opacity-100',
            )}
          />
        }
      >
        <StickyNote className={cn('size-3.5', hasNote && 'fill-green-100 text-green-700')} />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start" onClick={stopRowClick}>
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-dark">Account note</p>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note about this account…"
            rows={4}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={loading}>
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
